/**
 * ApiTokensCard — Personal API token management
 *
 * Mounted on /profile. Lets a user generate, view, and revoke API tokens
 * for Home Assistant / n8n / ESPHome / custom dashboards.
 *
 * Security UX contract:
 *   - Raw token (`tp_live_…`) is shown ONLY ONCE at creation time, in a
 *     persistent banner with a copy button. After that it's gone — the
 *     server only stores the SHA-256 hash.
 *   - Existing tokens are listed with prefix + label + last-used + revoke.
 *   - Revoking is a soft-delete (`revoked_at`); the row stays visible
 *     with an amber "Revoked" pill.
 *   - "Generate token" button auto-fills label hint (Home Assistant,
 *     etc.) so users don't ship blank-labeled tokens.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plug, Copy, Check, Trash, Warning, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

export const ApiTokensCard = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('Home Assistant');
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState(null);  // { raw_token, label }
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/profile/api-tokens`, { withCredentials: true });
      setTokens(res.data?.tokens || []);
    } catch (e) {
      // Silent — likely just unauth, the parent profile page handles redirect
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!label.trim()) { toast.error('Give the token a label so you remember where it lives.'); return; }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/profile/api-tokens`,
        { label: label.trim() },
        { withCredentials: true },
      );
      setJustCreated({
        raw_token: res.data.raw_token,
        label: res.data.token.label,
      });
      setShowCreate(false);
      setLabel('Home Assistant');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create token. Try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!justCreated?.raw_token) return;
    try {
      await navigator.clipboard.writeText(justCreated.raw_token);
      setCopied(true);
      toast.success('Token copied. Save it somewhere safe — you won\'t see it again.');
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error('Copy failed — select the text manually.'); }
  };

  const handleRevoke = async (id, currentLabel) => {
    if (!window.confirm(`Revoke "${currentLabel}"? Anything using this token (Home Assistant, dashboards) will stop working immediately.`)) return;
    try {
      await axios.delete(`${API_URL}/api/profile/api-tokens/${id}`, { withCredentials: true });
      toast.success('Token revoked.');
      await load();
    } catch (e) {
      toast.error('Could not revoke token.');
    }
  };

  const activeCount = tokens.filter((t) => !t.revoked_at).length;

  return (
    <Card data-testid="api-tokens-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Plug className="w-5 h-5 text-emerald-600" weight="duotone" />
          Personal API Tokens
        </CardTitle>
        <CardDescription>
          For Home Assistant, n8n, ESPHome, or any custom dashboard that needs to read your TurfPulse
          data. Read-only — tokens cannot fire schedules, change settings, or take destructive actions.{' '}
          <a href="/integrations/home-assistant" className="text-emerald-600 underline">
            Home Assistant setup guide →
          </a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* One-time raw-token banner */}
        {justCreated && (
          <div
            className="rounded-lg border-2 border-emerald-500/60 bg-emerald-500/5 p-3 space-y-2"
            data-testid="api-tokens-just-created"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <Warning className="w-4 h-4" weight="duotone" />
              Save this token NOW — you won't see it again
            </div>
            <div className="text-xs text-muted-foreground">
              Label: <span className="font-mono">{justCreated.label}</span>
            </div>
            <div className="flex items-stretch gap-2">
              <code
                className="flex-1 text-xs font-mono p-2 rounded bg-zinc-900 text-emerald-300 break-all select-all"
                data-testid="api-tokens-raw-value"
              >
                {justCreated.raw_token}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="shrink-0"
                data-testid="api-tokens-copy-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <button
              type="button"
              className="text-xs text-muted-foreground underline hover:text-foreground"
              onClick={() => { setJustCreated(null); setCopied(false); }}
              data-testid="api-tokens-dismiss-banner"
            >
              I've saved it — dismiss
            </button>
          </div>
        )}

        {/* Token list */}
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="text-xs text-muted-foreground italic" data-testid="api-tokens-empty">
            No tokens yet. Generate one to plug TurfPulse into Home Assistant.
          </div>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                className="flex items-start justify-between gap-3 p-2.5 rounded border border-border bg-muted/20"
                data-testid={`api-tokens-row-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{t.label}</span>
                    {t.revoked_at ? (
                      <Badge variant="outline" className="text-[9px] uppercase border-amber-500/40 text-amber-700 dark:text-amber-300">
                        Revoked
                      </Badge>
                    ) : (
                      <Badge className="text-[9px] uppercase bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                        Active
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                    {t.prefix}…{' · created '}{fmt(t.created_at)}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Last used: {fmt(t.last_used_at)}
                  </div>
                </div>
                {!t.revoked_at && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevoke(t.id, t.label)}
                    className="text-rose-600 hover:text-rose-700 shrink-0"
                    data-testid={`api-tokens-revoke-${t.id}`}
                  >
                    <Trash className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Generate */}
        {!showCreate ? (
          <Button
            variant="outline"
            onClick={() => setShowCreate(true)}
            className="w-full sm:w-auto"
            disabled={activeCount >= 10}
            data-testid="api-tokens-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {activeCount >= 10 ? 'Revoke an old token first (10 max)' : 'Generate new token'}
          </Button>
        ) : (
          <div className="space-y-2 p-3 rounded border border-border bg-muted/20">
            <Label htmlFor="api-token-label" className="text-xs">Label (where will this token live?)</Label>
            <Input
              id="api-token-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Home Assistant - Living Room Wall"
              maxLength={80}
              data-testid="api-tokens-label-input"
            />
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={creating} size="sm" data-testid="api-tokens-create-btn">
                {creating ? 'Generating…' : 'Generate token'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowCreate(false); setLabel('Home Assistant'); }}
                disabled={creating}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApiTokensCard;
